import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CustomerGroupDto, CustomerGroupRules, CustomerStage } from '@ecom/contracts';
import { STAGE_LABELS, StagesField } from '@/components/customer-stage';
import { AsyncSelect } from '@/components/async-select';
import { TagsField } from '@/components/fields';
import { FilterBar } from '@/components/filters';
import { PlusIcon, UsersIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Field, Input, Select, TableSkeleton } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, dateTime, money } from '@/lib/format';
import { categoryOptions, productOptions } from '@/lib/options';
import { halalasToRiyals, riyalsToHalalas } from '@/lib/price-input';
import { useCustomerGroupsStore } from '@/stores/customer-groups.store';

/** The rules as the form holds them: text, so an empty field is "no rule". */
interface RulesDraft {
  minOrders: string;
  maxOrders: string;
  minSpent: string;
  maxSpent: string;
  lastOrderWithinDays: string;
  lapsedDays: string;
  registeredWithinDays: string;
  city: string;
  tags: string[];
  boughtCategoryId: string;
  boughtProductId: string;
  stages: CustomerStage[];
  /** '' = either, 'yes' = agreed to marketing, 'no' = has not. */
  consent: '' | 'yes' | 'no';
  clickedWithinDays: string;
}

const COUNTS = ['minOrders', 'maxOrders', 'lastOrderWithinDays', 'lapsedDays', 'registeredWithinDays', 'clickedWithinDays'] as const;
const MONEY = ['minSpent', 'maxSpent'] as const;

function draftOf(rules: CustomerGroupRules | undefined): RulesDraft {
  const whole = (value: number | undefined) => (value === undefined ? '' : String(value));
  const riyals = (value: number | undefined) => (value === undefined ? '' : halalasToRiyals(value));
  return {
    minOrders: whole(rules?.minOrders),
    maxOrders: whole(rules?.maxOrders),
    minSpent: riyals(rules?.minSpent),
    maxSpent: riyals(rules?.maxSpent),
    lastOrderWithinDays: whole(rules?.lastOrderWithinDays),
    lapsedDays: whole(rules?.lapsedDays),
    registeredWithinDays: whole(rules?.registeredWithinDays),
    city: rules?.city ?? '',
    tags: rules?.tags ?? [],
    boughtCategoryId: rules?.boughtCategoryId ?? '',
    boughtProductId: rules?.boughtProductId ?? '',
    stages: rules?.stages ?? [],
    consent: rules?.acceptsMarketing === undefined ? '' : rules.acceptsMarketing ? 'yes' : 'no',
    clickedWithinDays: whole(rules?.clickedWithinDays),
  };
}

/** The rules the API is sent — null when a field is not a number it could take. */
function rulesOf(draft: RulesDraft): CustomerGroupRules | null {
  const rules: CustomerGroupRules = {};
  for (const field of COUNTS) {
    const text = draft[field].trim();
    if (!text) continue;
    if (!/^\d+$/.test(text)) return null;
    rules[field] = Number(text);
  }
  for (const field of MONEY) {
    const text = draft[field].trim();
    if (!text) continue;
    const halalas = riyalsToHalalas(text);
    if (halalas === null) return null;
    rules[field] = halalas;
  }
  if (draft.city.trim()) rules.city = draft.city.trim();
  if (draft.tags.length > 0) rules.tags = draft.tags;
  if (draft.boughtCategoryId) rules.boughtCategoryId = draft.boughtCategoryId;
  if (draft.boughtProductId) rules.boughtProductId = draft.boughtProductId;
  if (draft.stages.length > 0) rules.stages = draft.stages;
  if (draft.consent) rules.acceptsMarketing = draft.consent === 'yes';
  if (rules.clickedWithinDays === 0) return null;
  return rules;
}

/**
 * Customer groups by rules (ADR-085): each a name and rules, its members kept
 * and refreshed nightly — or now. A group's members open in the customer list;
 * a coupon or a campaign can be for one.
 */
export function CustomerGroupsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const navigate = useNavigate();
  const { items, nextCursor, total, search, loading, error, load, loadMore, refresh, saving } = useCustomerGroupsStore();
  const [editing, setEditing] = useState<CustomerGroupDto | 'new' | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const add = (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('customerGroupAdd')}
    </Button>
  );

  return (
    <Page title={t('customerGroups')} subtitle={t('customerGroupsSubtitle')} actions={add} fill>
      <ListCard>
        <ListToolbar>
          <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('customerGroupsSearch')} total={total ?? undefined} shown={items.length} />
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={4} />
        ) : items.length === 0 ? (
          <EmptyState title={t('customerGroupsEmpty')} body={t('customerGroupsEmptyHint')} icon={<UsersIcon />} action={add} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('customerGroup')}</Th>
                  <Th>{t('customerGroupRules')}</Th>
                  <Th>{t('customerGroupMembers')}</Th>
                  <Th>{t('customerGroupRefreshed')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items.map((group) => (
                  <Tr key={group.id} onClick={() => setEditing(group)}>
                    <Td className="font-medium text-ink">{group.name[locale] || group.name.en}</Td>
                    <Td className="max-w-96 text-sm text-ink-muted">{describe(group.rules, t, locale)}</Td>
                    <Td data-numeric>
                      <button
                        type="button"
                        className="font-medium text-brand-700 hover:underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/customers?group=${group.id}`);
                        }}
                      >
                        {count(group.memberCount, locale)}
                      </button>
                    </Td>
                    <Td className="whitespace-nowrap text-ink-muted">{group.refreshedAt ? dateTime(group.refreshedAt, locale) : '—'}</Td>
                    <Td>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={saving}
                        onClick={(event) => {
                          event.stopPropagation();
                          void refresh(group.id);
                        }}
                      >
                        {t('customerGroupRefresh')}
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? <EditSheet group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Page>
  );
}

/** A group's rules in one line, as a person would say them. */
function describe(rules: CustomerGroupRules, t: ReturnType<typeof useT>, locale: 'ar' | 'en'): string {
  const parts: string[] = [];
  if (rules.minOrders !== undefined) parts.push(t('ruleMinOrders', { count: String(rules.minOrders) }));
  if (rules.maxOrders !== undefined) parts.push(t('ruleMaxOrders', { count: String(rules.maxOrders) }));
  if (rules.minSpent !== undefined) parts.push(t('ruleMinSpent', { amount: money(rules.minSpent, locale) }));
  if (rules.maxSpent !== undefined) parts.push(t('ruleMaxSpent', { amount: money(rules.maxSpent, locale) }));
  if (rules.lastOrderWithinDays !== undefined) parts.push(t('ruleLastOrderWithin', { days: String(rules.lastOrderWithinDays) }));
  if (rules.lapsedDays !== undefined) parts.push(t('ruleLapsed', { days: String(rules.lapsedDays) }));
  if (rules.registeredWithinDays !== undefined) parts.push(t('ruleRegisteredWithin', { days: String(rules.registeredWithinDays) }));
  if (rules.city) parts.push(rules.city);
  if (rules.tags?.length) parts.push(rules.tags.join(', '));
  if (rules.boughtCategoryId) parts.push(t('ruleBoughtCategory'));
  if (rules.boughtProductId) parts.push(t('ruleBoughtProduct'));
  if (rules.stages?.length) parts.push(t('ruleStages', { stages: rules.stages.map((stage) => t(STAGE_LABELS[stage])).join(locale === 'ar' ? '، ' : ', ') }));
  if (rules.acceptsMarketing !== undefined) parts.push(t(rules.acceptsMarketing ? 'ruleConsentYes' : 'ruleConsentNo'));
  if (rules.clickedWithinDays !== undefined) parts.push(t('ruleClickedWithin', { days: String(rules.clickedWithinDays) }));
  return parts.join(' · ');
}

function EditSheet({ group, onClose }: { group: CustomerGroupDto | null; onClose: () => void }) {
  const t = useT();
  const { save, remove, preview, previewCount, saving, saveError, clearPreview } = useCustomerGroupsStore();
  const [nameEn, setNameEn] = useState(group?.name.en ?? '');
  const [nameAr, setNameAr] = useState(group?.name.ar ?? '');
  const [draft, setDraft] = useState<RulesDraft>(draftOf(group?.rules));
  const [deleting, setDeleting] = useState(false);
  const rules = rulesOf(draft);
  const hasRule = rules !== null && Object.keys(rules).length > 0;
  const ready = Boolean(nameEn.trim() || nameAr.trim()) && hasRule;
  const set = (patch: Partial<RulesDraft>) => setDraft((current) => ({ ...current, ...patch }));

  useEffect(() => {
    clearPreview();
  }, [clearPreview]);

  // Who the rules take in, asked as they change — a moment after the typing stops.
  const key = JSON.stringify(rules);
  useEffect(() => {
    if (!hasRule || !rules) return;
    const timer = setTimeout(() => void preview(rules), 500);
    return () => clearTimeout(timer);
    // `key` carries the rules' content; the object itself is new each render.
  }, [key, hasRule, preview]);

  async function submit() {
    if (!ready || !rules) return;
    if (await save(group?.id ?? null, { name: { en: nameEn.trim(), ar: nameAr.trim() }, rules })) onClose();
  }

  const number = (field: keyof RulesDraft, label: string, hint?: string) => (
    <Field label={label} htmlFor={`group-${field}`} hint={hint}>
      <Input
        id={`group-${field}`}
        dir="ltr"
        inputMode="numeric"
        value={draft[field] as string}
        onChange={(event) => set({ [field]: event.target.value } as Partial<RulesDraft>)}
      />
    </Field>
  );

  return (
    <>
      <Sheet
        open
        size="lg"
        title={group ? group.name.en || group.name.ar : t('customerGroupAdd')}
        subtitle={t('customerGroupEditHint')}
        onClose={() => {
          if (!deleting) onClose();
        }}
        footer={
          <>
            {group ? (
              <Button variant="secondary" className="me-auto text-danger" onClick={() => setDeleting(true)} disabled={saving}>
                {t('delete')}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button disabled={!ready || saving} onClick={() => void submit()}>
              {t('save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('nameArabic')} htmlFor="group-name-ar">
              <Input id="group-name-ar" dir="rtl" maxLength={80} value={nameAr} onChange={(event) => setNameAr(event.target.value)} />
            </Field>
            <Field label={t('nameEnglish')} htmlFor="group-name-en">
              <Input id="group-name-en" dir="ltr" maxLength={80} value={nameEn} onChange={(event) => setNameEn(event.target.value)} />
            </Field>
          </div>
          <p className="text-xs text-ink-muted">{t('customerGroupRulesHint')}</p>
          <StagesField id="group-stages" label={t('ruleStagesLabel')} value={draft.stages} onChange={(stages) => set({ stages })} />
          <div className="grid gap-4 sm:grid-cols-2">
            {number('minOrders', t('ruleMinOrdersLabel'))}
            {number('maxOrders', t('ruleMaxOrdersLabel'))}
            {number('minSpent', t('ruleMinSpentLabel'))}
            {number('maxSpent', t('ruleMaxSpentLabel'))}
            {number('lastOrderWithinDays', t('ruleLastOrderWithinLabel'))}
            {number('lapsedDays', t('ruleLapsedLabel'), t('ruleLapsedHint'))}
            {number('registeredWithinDays', t('ruleRegisteredWithinLabel'))}
            <Field label={t('city')} htmlFor="group-city">
              <Input id="group-city" maxLength={80} value={draft.city} onChange={(event) => set({ city: event.target.value })} />
            </Field>
            {number('clickedWithinDays', t('ruleClickedWithinLabel'))}
            <Field label={t('ruleConsentLabel')} htmlFor="group-consent">
              <Select id="group-consent" value={draft.consent} onChange={(event) => set({ consent: event.target.value as RulesDraft['consent'] })}>
                <option value="">{t('anyValue')}</option>
                <option value="yes">{t('ruleConsentYes')}</option>
                <option value="no">{t('ruleConsentNo')}</option>
              </Select>
            </Field>
          </div>
          <Field label={t('campaignBoughtProduct')} htmlFor="group-product">
            <AsyncSelect
              id="group-product"
              value={draft.boughtProductId}
              emptyLabel={t('anyValue')}
              fetchPage={(search, cursor) => productOptions.page(search, cursor)}
              fetchOne={(id) => productOptions.one(id)}
              onChange={(id) => set({ boughtProductId: id })}
            />
          </Field>
          <Field label={t('campaignBoughtCategory')} htmlFor="group-category">
            <AsyncSelect
              id="group-category"
              value={draft.boughtCategoryId}
              emptyLabel={t('anyValue')}
              fetchPage={(search, cursor) => categoryOptions.page(search, cursor)}
              fetchOne={(id) => categoryOptions.one(id)}
              onChange={(id) => set({ boughtCategoryId: id })}
            />
          </Field>
          <TagsField id="group-tags" label={t('customerTags')} value={draft.tags} onChange={(tags) => set({ tags })} />
          {rules === null ? <Alert>{t('customerGroupBadNumber')}</Alert> : null}
          {hasRule && previewCount !== null ? <Alert tone="info">{t('customerGroupPreview', { count: String(previewCount) })}</Alert> : null}
          {saveError ? <Alert>{saveError}</Alert> : null}
        </div>
      </Sheet>
      <ConfirmDialog
        open={deleting}
        title={t('customerGroupDeleteTitle')}
        body={t('customerGroupDeleteBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (group && (await remove(group.id))) onClose();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}
